import { defineAsyncComponent } from '../../lib/utils/vue3compat/vue.js?vue3';
import { GlForm, GlModal, GlAlert, GlButton } from '../../../../../node_modules/@gitlab/ui/src/index.js?vue3';
import { visitUrl } from '~/lib/utils/url_utility';
import { __, s__ } from '~/locale';
import { formType } from '../constants';
import { setError } from '../graphql/cache_updates.js?vue3';
import errorQuery from '../graphql/client/error.query.graphql';
import createBoardMutation from '../graphql/board_create.mutation.graphql';
import destroyBoardMutation from '../graphql/board_destroy.mutation.graphql';
import updateBoardMutation from '../graphql/board_update.mutation.graphql';
import BoardConfigurationOptions from './board_configuration_options.vue?vue3';
const boardDefaults = {
    id: false,
    name: '',
    labels: [],
    milestone: {},
    iterationCadence: {},
    iterationCadenceId: null,
    iteration: {},
    assignee: {},
    weight: null,
    hideBacklogList: false,
    hideClosedList: false
};
export default {
    name: 'BoardForm',
    i18n: {
        [formType.new]: {
            title: s__('Boards|Create new board'),
            btnText: s__('Boards|Create board')
        },
        [formType.delete]: {
            title: s__('Boards|Delete board'),
            btnText: __('Delete')
        },
        [formType.edit]: {
            title: s__('Boards|Configure board'),
            btnText: __('Save changes')
        },
        scopeModalTitle: s__('Boards|Board configuration'),
        cancelButtonText: __('Cancel'),
        deleteButtonText: s__('Boards|Delete board'),
        deleteErrorMessage: s__('Boards|Failed to delete board. Please try again.'),
        saveErrorMessage: __('Unable to save your changes. Please try again.'),
        deleteConfirmationMessage: s__('Boards|Are you sure you want to delete this board?'),
        lastBoardDefaultMessage: s__('Boards|Because this is the only board here, when this board is deleted, a new default Development board will be created.'),
        lastBoardGroupMessage: s__('Boards|Because this is the only board in this group, when this board is deleted, a new default Development board will be created.'),
        lastBoardProjectMessage: s__('Boards|Because this is the only board in this project, when this board is deleted, a new default Development board will be created.'),
        titleFieldLabel: __('Title'),
        titleFieldPlaceholder: s__('Boards|Enter board name')
    },
    components: {
        BoardScope: defineAsyncComponent(()=>import('../../../../../ee/app/assets/javascripts/boards/components/board_scope.vue?vue3')),
        GlModal,
        GlButton,
        BoardConfigurationOptions,
        GlAlert,
        GlForm
    },
    inject: {
        fullPath: {
            default: ''
        },
        boardBaseUrl: {
            default: ''
        },
        isGroupBoard: {
            default: false
        },
        isProjectBoard: {
            default: false
        }
    },
    props: {
        canAdminBoard: {
            type: Boolean,
            required: true
        },
        scopedIssueBoardFeatureEnabled: {
            type: Boolean,
            required: false,
            default: false
        },
        weights: {
            type: Array,
            required: false,
            default: ()=>[]
        },
        currentBoard: {
            type: Object,
            required: true
        },
        currentPage: {
            type: String,
            required: true
        },
        isLastBoard: {
            type: Boolean,
            required: false,
            default: false
        },
        parentType: {
            type: String,
            required: false,
            default: null
        }
    },
    emits: [
        'add-board',
        'cancel',
        'show-board-modal',
        'shown',
        'update-board'
    ],
    data () {
        return {
            error: undefined,
            board: {
                ...boardDefaults,
                ...this.currentBoard
            },
            isLoading: false,
            isDisabled: false
        };
    },
    apollo: {
        error: {
            query: errorQuery,
            update: (data)=>data.boardsAppError
        }
    },
    computed: {
        isNewForm () {
            return this.currentPage === formType.new;
        },
        isDeleteForm () {
            return this.currentPage === formType.delete;
        },
        isEditForm () {
            return this.currentPage === formType.edit;
        },
        buttonText () {
            return this.$options.i18n[this.currentPage].btnText;
        },
        buttonKind () {
            if (this.isDeleteForm) {
                return 'danger';
            }
            return 'confirm';
        },
        title () {
            if (this.readonly) {
                return this.$options.i18n.scopeModalTitle;
            }
            return this.$options.i18n[this.currentPage].title;
        },
        readonly () {
            return !this.canAdminBoard;
        },
        submitDisabled () {
            return this.isLoading || this.board.name.length === 0 || this.isDisabled;
        },
        primaryProps () {
            return {
                text: this.buttonText,
                attributes: {
                    variant: this.buttonKind,
                    disabled: this.submitDisabled,
                    loading: this.isLoading,
                    'data-testid': 'save-changes-button'
                }
            };
        },
        cancelProps () {
            return {
                text: this.$options.i18n.cancelButtonText
            };
        },
        currentMutation () {
            return this.board.id ? updateBoardMutation : createBoardMutation;
        },
        deleteMutation () {
            return destroyBoardMutation;
        },
        baseMutationVariables () {
            const { board: { name, hideBacklogList, hideClosedList, id } } = this;
            const variables = {
                name,
                hideBacklogList,
                hideClosedList
            };
            return id ? {
                ...variables,
                id
            } : {
                ...variables,
                projectPath: this.isProjectBoard ? this.fullPath : undefined,
                groupPath: this.isGroupBoard ? this.fullPath : undefined
            };
        },
        mutationVariables () {
            return this.baseMutationVariables;
        },
        canDelete () {
            return this.canAdminBoard && this.isEditForm;
        },
        lastBoardMessage () {
            if (this.parentType === 'group') {
                return this.$options.i18n.lastBoardGroupMessage;
            }
            if (this.parentType === 'project') {
                return this.$options.i18n.lastBoardProjectMessage;
            }
            return this.$options.i18n.lastBoardDefaultMessage;
        }
    },
    watch: {
        currentBoard (board) {
            if (!this.isNewForm && !this.board.id && Object.keys(board).length) {
                this.resetFormState();
            }
        }
    },
    mounted () {
        this.resetFormState();
        if (this.$refs.name) {
            this.$refs.name.focus();
        }
        this.$emit('shown');
    },
    methods: {
        setError,
        isFocusMode () {
            return Boolean(document.querySelector('.content-wrapper > .js-focus-mode-board.is-focused') || document.querySelector('.js-content-panels.is-focused'));
        },
        cancel () {
            this.$emit('cancel');
        },
        close () {
            // This calls cancel after the modal has been hidden
            this.$refs.modal.hide();
        },
        async createOrUpdateBoard () {
            const response = await this.$apollo.mutate({
                mutation: this.currentMutation,
                variables: {
                    input: this.mutationVariables
                }
            });
            if (!this.board.id) {
                return response.data.createBoard.board;
            }
            return response.data.updateBoard.board;
        },
        openDeleteModal () {
            this.$emit('show-board-modal', this.$options.formType.delete);
        },
        async deleteBoard () {
            await this.$apollo.mutate({
                mutation: this.deleteMutation,
                variables: {
                    id: this.board.id
                }
            });
        },
        async submit () {
            if (this.board.name.length === 0) return;
            this.isLoading = true;
            if (this.isDeleteForm) {
                try {
                    await this.deleteBoard();
                    visitUrl(this.boardBaseUrl);
                    this.close();
                } catch (error) {
                    setError({
                        error,
                        message: this.$options.i18n.deleteErrorMessage
                    });
                } finally{
                    this.isLoading = false;
                    this.isDisabled = true;
                }
            } else {
                try {
                    const board = await this.createOrUpdateBoard();
                    if (this.board.id) {
                        this.$emit('update-board', board);
                    } else {
                        this.$emit('add-board', board);
                    }
                    this.close();
                } catch (error) {
                    setError({
                        error,
                        message: this.$options.i18n.saveErrorMessage
                    });
                } finally{
                    this.isLoading = false;
                    this.isDisabled = true;
                }
            }
        },
        resetFormState () {
            if (this.isNewForm) {
                // Clear the form when we open the "New board" modal
                this.board = {
                    ...boardDefaults
                };
            } else if (this.currentBoard && Object.keys(this.currentBoard).length) {
                this.board = {
                    ...boardDefaults,
                    ...this.currentBoard
                };
            }
        },
        setIteration (iteration) {
            this.board.iterationCadenceId = iteration.iterationCadenceId;
            this.board = {
                ...this.board,
                iteration: {
                    id: iteration.id
                }
            };
        },
        setBoardLabels (labels) {
            this.board.labels = labels;
        },
        setAssignee (assigneeId) {
            this.board = {
                ...this.board,
                assignee: {
                    id: assigneeId
                }
            };
        },
        setMilestone (milestoneId) {
            this.board = {
                ...this.board,
                milestone: {
                    id: milestoneId
                }
            };
        },
        setWeight (weight) {
            this.board = {
                ...this.board,
                weight
            };
        }
    },
    formType
};
